import {
  Button,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemText,
  Loader,
  Paper,
  Tooltip,
  Typography,
} from 'apollo-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getIsWorkflowListLoading } from '../../selectors/export.selectors';

const not = (a, b) => {
  return a.filter((value) => !b.includes(value));
};

const intersection = (a, b) => {
  return a.filter((value) => b.includes(value));
};

const sortItemsAlphabetically = (items = []) => items.slice().sort((a, b) => a.localeCompare(b));

const TransferList = ({
  listData = [],
  selectedItems = [],
  leftTitle = '',
  rightTitle = '',
  handleSelectItems,
}) => {
  const translateString = useTranslation();
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(listData.filter((item) => !selectedItems.includes(item)));
  const [right, setRight] = useState(selectedItems);

  const isWorkflowListLoading = useSelector(getIsWorkflowListLoading);

  useEffect(() => {
    setLeft(listData.filter((item) => !selectedItems.includes(item)));
  }, [listData, selectedItems]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value) => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const updateRight = (items = []) => {
    setRight(items);
    handleSelectItems(items);
  };

  const handleAllRight = () => {
    const updatedRight = right.concat(left);
    const rightSortedItems = sortItemsAlphabetically(updatedRight);

    updateRight(rightSortedItems);
    setChecked([]);
    setLeft([]);
  };

  const handleCheckedRight = () => {
    const updatedRight = right.concat(leftChecked);
    const rightSortedItems = sortItemsAlphabetically(updatedRight);
    const updatedLeft = not(left, leftChecked);

    updateRight(rightSortedItems);
    setLeft(sortItemsAlphabetically(updatedLeft));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    const updatedLeft = left.concat(rightChecked);
    const updatedRight = not(right, rightChecked);
    const rightSortedItems = sortItemsAlphabetically(updatedRight);

    setLeft(sortItemsAlphabetically(updatedLeft));
    updateRight(rightSortedItems);
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setChecked([]);
    updateRight([]);
  };

  const customList = ({ items = [], title, isLoading = false }) => (
    <>
      <Typography sx={{ marginBottom: 1 }}>{title}</Typography>
      <Paper sx={{ width: 420, height: 300, overflow: 'auto', position: 'relative' }}>
        {isLoading ? (
          <Loader isInner />
        ) : (
          <List>
            {items.map((value) => {
              return (
                <ListItem
                  sx={{ height: 20, cursor: 'pointer' }}
                  key={value}
                  onClick={() => handleToggle(value)}
                >
                  <Checkbox
                    checked={checked.includes(value)}
                    style={{ width: '40px' }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggle(value)}
                  />
                  <ListItemText primary={value} />
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    </>
  );

  const selectAll = '≫';
  const selectPart = '&gt';
  const unselectPart = '&lt;';
  const unselectAll = '≪';

  return (
    <Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
      <Grid item>
        {customList({ items: left, title: leftTitle, isLoading: isWorkflowListLoading })}
      </Grid>
      <Grid item>
        <Grid container direction="column" sx={{ alignItems: 'center' }}>
          <Tooltip title={translateString('To Right')} placement="right">
            <span>
              <Button
                sx={{ my: 0.5 }}
                variant="secondary"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
                aria-label="To Right"
              >
                <div dangerouslySetInnerHTML={{ __html: selectPart }} />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={translateString('To Left')} placement="right">
            <span>
              <Button
                sx={{ my: 0.5 }}
                variant="secondary"
                size="small"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label="To Left"
              >
                <div dangerouslySetInnerHTML={{ __html: unselectPart }} />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={translateString('All to Right')} placement="right">
            <span>
              <Button
                sx={{ my: 0.5 }}
                variant="secondary"
                size="small"
                onClick={handleAllRight}
                disabled={left.length === 0}
                aria-label="All to Right"
              >
                <div dangerouslySetInnerHTML={{ __html: selectAll }} />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={translateString('All to Left')} placement="right">
            <span>
              <Button
                sx={{ my: 0.5 }}
                variant="secondary"
                size="small"
                onClick={handleAllLeft}
                disabled={right.length === 0}
                aria-label="All to Left"
              >
                <div dangerouslySetInnerHTML={{ __html: unselectAll }} />
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid item>{customList({ items: right, title: rightTitle })}</Grid>
    </Grid>
  );
};

export default TransferList;
